import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const headers = () => {
  const token = localStorage.getItem('session_token');
  return { 'Content-Type': 'application/json', ...(token ? { 'X-Session-Token': token } : {}) };
};

const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const label = `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
  return { value, label };
});

const Toggle = ({ checked, onChange, testId }) => (
  <button data-testid={testId} role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 min-h-0 flex-shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

const PrefRow = ({ title, hint, testId, checked, onChange }) => (
  <div className="flex items-center justify-between gap-3">
    <div>
      <p className="text-sm text-gray-800 dark:text-gray-200">{title}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
    </div>
    <Toggle testId={testId} checked={checked} onChange={onChange} />
  </div>
);

export const NotificationPrefs = () => {
  const [prefs, setPrefs] = useState(null);
  const [saved, setSaved] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/users/me/notification-prefs`, { credentials: 'include', headers: headers() })
      .then((r) => (r.ok ? r.json() : null)).then(setPrefs).catch(() => {});
  }, []);

  const update = async (change) => {
    setPrefs((p) => ({ ...p, ...change }));
    const res = await fetch(`${API_URL}/api/users/me/notification-prefs`, {
      method: 'PUT', credentials: 'include', headers: headers(), body: JSON.stringify(change)
    });
    if (res.ok) {
      setPrefs(await res.json());
      setSaved('Saved');
      setTimeout(() => setSaved(''), 1500);
    } else {
      setSaved('Could not save, try again');
    }
  };

  if (!prefs) return null;
  return (
    <div data-testid="notification-prefs" className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">Choose your notifications</p>
        {saved && <span data-testid="notification-prefs-saved" className="text-xs text-green-600">{saved}</span>}
      </div>
      {typeof Notification !== 'undefined' && Notification.permission !== 'granted' && (
        <p data-testid="notification-prefs-push-hint" className="text-xs text-amber-600">Turn on Push Notifications above to receive these.</p>
      )}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-800 dark:text-gray-200">Daily reminder</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">On days you have a workout scheduled</p>
        </div>
        <Toggle testId="reminder-enabled-toggle" checked={prefs.reminders_enabled} onChange={(v) => update({ reminders_enabled: v })} />
      </div>
      {prefs.reminders_enabled && (
        <select data-testid="reminder-time-select" value={prefs.reminder_time} onChange={(e) => update({ reminder_time: e.target.value })}
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          {TIME_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      )}
      <PrefRow title="7 PM nudge" hint="If today's workout isn't done yet" testId="nudge-enabled-toggle"
        checked={prefs.nudge_enabled} onChange={(v) => update({ nudge_enabled: v })} />
      <PrefRow title="Messages from your trainer" hint="New chat messages" testId="messages-enabled-toggle"
        checked={prefs.messages_enabled} onChange={(v) => update({ messages_enabled: v })} />
      <PrefRow title="Rest timer alerts" hint="When your rest between sets is over" testId="rest-timer-enabled-toggle"
        checked={prefs.rest_timer_enabled} onChange={(v) => update({ rest_timer_enabled: v })} />
      <PrefRow title="Streak celebrations" hint="When you hit a 3, 7 or 30-day streak" testId="streaks-enabled-toggle"
        checked={prefs.streaks_enabled} onChange={(v) => update({ streaks_enabled: v })} />
    </div>
  );
};
